<template>
  <div>
    <PageAllow controller="ProductionPlans" action="autoSplitStockIns" />
    <Breadcrumbs :items="items" />
    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5 mt-1">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRange
              type="datetime"
              v-model="datetimeRage"
              :label="$t('AutoSplitProduction.Fields.created')"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="$permission.permissionAllow('ProductionPlans', 'add')"
              small
              color="primary"
              @click.stop="addAutoSplitProduction()"
              class="ms-1"
            >
              <v-icon x-small class="pe-1">fa-plus</v-icon>
              {{ $t("AutoSplitProduction.action.addNewproductionPlan") }}
            </v-btn>
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherStock v-model="stockSelected" :stock-types="[0]" unclearable />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="stock_order_code"
                  v-model="options.stock_order_code"
                  @text="stockInsKeyWord = $event"
                  :label="$t('AutoSplitProduction.stockInsKeyword')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="stockInTypeSelected"
                  clearable
                  :items="$consts.stock_in_order.types"
                  :label="$t('StockOrderIN.Fields.stock_in_type')"
                  disabled
                >
                </v-select>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherMaterialCategory v-model="categorySelected" />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="keyword"
                  v-model="options.key_word"
                  @text="materialKeyword = $event"
                  :label="$t('AutoSplitProduction.materialKeyword')"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          v-model="showSelected"
          :headers="headers"
          :items="purchaseDetailItems"
          :loading="loading"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          multi-sort
          show-select
          class="elevation-2 px-4 pt-1"
          :footer-props="$t('$config.footer-props')"
          item-key="detail_id"
        >
          <template v-slot:item.confirm_num="{ item }">
            {{ item.confirm_num | formatTwoDot }}
            {{ item.unit }}
          </template>
          <template v-slot:item.stock_order_code="{ item }">
            <span class="text-no-wrap caption font-weight-bold">
              {{ item.stock_order_code }}
            </span>
            <br />
            <span
              class="text-no-wrap caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.created | formatTime }}
            </span>
          </template>
          <template v-slot:item.type="{ item }">
            {{ item.type | stockOrderINTypesText }}
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import { mapState } from "vuex";
import SearcherDatetimeRange from "@/components/plugins/SearcherDatetimeRange";
import SearcherStock from "@/components/plugins/SearcherStock";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherMaterialCategory from "@/components/plugins/SearcherMaterialCategory";
import ProductionPlans from "@/api/production_plans";

const defaultOptions = {
  created_time_from:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  created_time_to: new Date().toISOString().substr(0, 10) + " 23:59:59",
  stock_id: 0,
  type: 11,
  stock_order_code: "",
  key_word: "",
  category_codes: [],
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRange,
    SearcherKeyword,
    SearcherStock,
    SearcherMaterialCategory,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.productionManagement"),
        disabled: false,
        replace: true,
        href: "/production-plans",
      },
      {
        text: i18n.t("Nav.autoSplitProduction"),
        disabled: true,
        href: "/auto-split-production",
      },
    ],

    headers() {
      return [
        {
          text: i18n.t("AutoSplitProduction.Fields.material"),
          align: "start",
          sortable: false,
          value: "Description",
        },
        {
          text: i18n.t("AutoSplitProduction.Fields.stock_in_num"),
          align: "start",
          sortable: false,
          value: "confirm_num",
        },
        {
          text:
            i18n.t("AutoSplitProduction.Fields.stock_in_code") +
            " / " +
            i18n.t("AutoSplitProduction.Fields.created"),
          align: "start",
          sortable: false,
          value: "stock_order_code",
        },
        {
          text: i18n.t("AutoSplitProduction.Fields.stock_in_type"),
          align: "start",
          sortable: false,
          value: "type",
        },
      ];
    },

    purchaseDetailItems() {
      return this.purchase_details.map((detail) => {
        detail.Description = i18n.t(
          this.settings
            ? this.settings.material_name_display_type == 4
              ? "Material.nameWithCodeAndStandard"
              : this.settings.material_name_display_type == 3
              ? "Material.materialNameWithCodeAndStandard"
              : this.settings.material_name_display_type == 2
              ? "Material.materialNameWithCode"
              : "Material.materialName"
            : "Material.materialName",
          [detail.material_code, detail.material_name, detail.standard_name]
        );

        return detail;
      });
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings"]),
  },

  data: () => ({
    showSelected: [],
    purchase_details: [],
    paging: {},
    options: JSON.parse(
      sessionStorage.getItem("auto-split-production-options")
        ? sessionStorage.getItem("auto-split-production-options")
        : JSON.stringify(defaultOptions)
    ),
    datetimeRage: [
      new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
        .toISOString()
        .substr(0, 10) + " 00:00:00",
      new Date().toISOString().substr(0, 10) + " 23:59:59",
    ],
    stockInsKeyWord: "",
    stockSelected: null,
    stockInTypeSelected: 11,
    categorySelected: null,
    materialKeyword: "",
  }),

  mounted() {
    this.$commonUtils
      .loadOptions("auto-split-production", ["stockSelected"])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
            if (
              typeof this.options.created_time_from != "undefined" &&
              typeof this.options.created_time_to != "undefined"
            )
              this.datetimeRage = [
                this.options.created_time_from,
                this.options.created_time_to,
              ];
          } else this.resetSearch();

          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "stockSelected":
                this.stockSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  methods: {
    resetSearch() {
      this.stockSelected = null;
      this.categorySelected = null;
      this.stockInTypeSelected = 11;
      this.datetimeRage = [
        new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
          .toISOString()
          .substr(0, 10) + " 00:00:00",
        new Date().toISOString().substr(0, 10) + " 23:59:59",
      ];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.stock_order_code, this.$refs.keyword].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        stock_order_code: this.stockInsKeyWord,
        key_word: this.materialKeyword,
      });
    },
    getPurchaseDetailsFromAPI() {
      var options = JSON.parse(JSON.stringify(this.options));
      ProductionPlans.autoSplitProduction(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.purchase_details = response.data.purchase_details;
            this.paging = response.data.paging;
          }
        }.bind(this)
      );
    },
    addAutoSplitProduction() {
      if (this.showSelected.length) {
        let selectedIds = [];
        this.showSelected.map((detail) => {
          selectedIds.push(Number(detail.detail_id));
        });
        this.$router.push({
          path: "/production-plans/new",
          query: {
            stock: encodeURIComponent(JSON.stringify(this.stockSelected)),
            showSelected: encodeURIComponent(JSON.stringify(selectedIds)),
          },
        });
      }
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getPurchaseDetailsFromAPI();
        sessionStorage.setItem(
          "auto-split-production-options",
          JSON.stringify(this.options)
        );
      },
    },
    stockSelected: {
      deep: true,
      handler() {
        if (this.stockSelected) {
          this.options.stock_id = this.stockSelected.id;
        } else {
          this.options.stock_id = "";
        }
        sessionStorage.setItem(
          "auto-split-production-stockSelected",
          JSON.stringify(this.stockSelected)
        );
      },
    },
    categorySelected: {
      handler() {
        this.options.category_codes = this.categorySelected
          ? this.categorySelected.code
          : "";
        sessionStorage.setItem(
          "auto-split-production-categorySelected",
          JSON.stringify(this.categorySelected)
        );
      },
    },
    datetimeRage: {
      handler() {
        if (this.datetimeRage[0] == null || this.datetimeRage[0] == "") {
          this.options.created_time_from =
            new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
              .toISOString()
              .substr(0, 10) + " 00:00:00";
        } else {
          this.options.created_time_from = this.datetimeRage[0];
        }

        if (this.datetimeRage[1] == null || this.datetimeRage[1] == "") {
          this.options.created_time_to =
            new Date().toISOString().substr(0, 10) + " 23:59:59";
        } else {
          this.options.created_time_to = this.datetimeRage[1];
        }
      },
    },
    showSelected: {
      handler() {},
    },
  },
};
</script>